/**
 * TASK-ATTR-012: Effect Playback System
 *
 * Manages playing effect animations on the battlefield.
 * Handles sprite positioning, playback, and auto-cleanup.
 */

import * as PIXI from 'pixi.js';
import { EffectSpritePool } from './effect-sprite-pool';

/**
 * Options for playing an effect
 */
export interface EffectPlaybackOptions {
  /** Screen position to play the effect at */
  position: { x: number; y: number };

  /** Rotation in radians (default: 0) */
  rotation?: number;

  /** Scale factor (default: 1.0) */
  scale?: number;

  /** Blend mode for the effect (default: 'normal') */
  blendMode?: string;

  /** Callback when animation completes */
  onComplete?: () => void;

  /** Whether to loop the animation (default: false) */
  loop?: boolean;
}

/**
 * Internal tracking for active effect playback
 */
interface ActiveEffect {
  effectId: string;
  attributeId: string;
  sprite: PIXI.AnimatedSprite;
  onComplete?: () => void;
}

/**
 * System for playing effect animations
 * Manages sprite positioning, playback state, and auto-cleanup
 */
export class EffectPlaybackSystem {
  private pool: EffectSpritePool;
  private container: PIXI.Container;
  private activeEffects = new Map<string, ActiveEffect>();
  private nextEffectId = 0;

  constructor(pool: EffectSpritePool, container: PIXI.Container) {
    this.pool = pool;
    this.container = container;
  }

  /**
   * Play an effect animation
   *
   * @param effectId - Unique identifier for this effect instance
   * @param frames - Array of base64-encoded PNG frames
   * @param options - Playback options
   */
  play(effectId: string, frames: string[], options: EffectPlaybackOptions): void {
    // Stop any existing effect with this ID
    this.stop(effectId);

    // Get sprite from pool
    const sprite = this.pool.acquire(effectId, frames);

    // Apply positioning
    sprite.x = options.position.x;
    sprite.y = options.position.y;
    sprite.anchor.set(0.5, 0.5); // Center the sprite

    // Apply rotation
    if (options.rotation !== undefined) {
      sprite.rotation = options.rotation;
    }

    // Apply scale
    if (options.scale !== undefined) {
      sprite.scale.set(options.scale);
    }

    // Apply blend mode
    if (options.blendMode !== undefined) {
      sprite.blendMode = options.blendMode as any; // PIXI v8 uses string literals for blendMode
    }

    // Set loop mode
    sprite.loop = options.loop || false;

    // Set up completion handler
    const onCompleteHandler = () => {
      // Remove event listener
      sprite.onComplete = null;

      // Call user callback
      if (options.onComplete) {
        options.onComplete();
      }

      // Auto-cleanup (remove from container and return to pool)
      this.stop(effectId);
    };

    sprite.onComplete = onCompleteHandler;

    // Add to container
    this.container.addChild(sprite);

    // Start animation
    sprite.play();

    // Track the active effect
    this.activeEffects.set(effectId, {
      effectId,
      attributeId: effectId,
      sprite,
      onComplete: options.onComplete
    });
  }

  /**
   * Stop an effect animation
   * Removes the sprite from the container and returns it to the pool
   *
   * @param effectId - Effect instance ID to stop
   */
  stop(effectId: string): void {
    const effect = this.activeEffects.get(effectId);
    if (!effect) {
      return;
    }

    // Stop animation
    effect.sprite.stop();

    // Remove completion handler
    effect.sprite.onComplete = null;

    // Remove from container
    if (effect.sprite.parent) {
      this.container.removeChild(effect.sprite);
    }

    // Return to pool
    this.pool.release(effect.sprite);

    // Remove from tracking
    this.activeEffects.delete(effectId);
  }

  /**
   * Update the playback system (called every frame)
   * Handles any per-frame updates needed
   *
   * @param _delta - Time delta in seconds (currently unused)
   */
  update(_delta: number): void {
    // Currently no per-frame updates needed
    // Animation state is managed by PIXI.AnimatedSprite
    // Cleanup happens in onComplete handlers
  }

  /**
   * Stop all active effects
   */
  stopAll(): void {
    // Get all effect IDs
    const effectIds = Array.from(this.activeEffects.keys());

    // Stop each effect
    for (const effectId of effectIds) {
      this.stop(effectId);
    }
  }

  /**
   * Generate a unique effect ID
   * Can be used when the caller doesn't need to track the effect ID
   *
   * @returns Unique effect ID
   */
  generateEffectId(): string {
    return `effect_${this.nextEffectId++}`;
  }

  /**
   * Get statistics about active effects
   */
  getStats() {
    return {
      activeEffects: this.activeEffects.size,
      poolStats: this.pool.getStats()
    };
  }

  /**
   * Destroy the playback system
   */
  destroy(): void {
    // Stop all effects
    this.stopAll();

    // Clear tracking
    this.activeEffects.clear();
  }
}
